import React, { useEffect, useRef, useState } from 'react';
import Container from 'react-bootstrap/Container';
import { connect } from 'react-redux';
import { Route, Switch, useHistory, useLocation } from 'react-router-dom';

import PrivateSubmissionDetailPage from '@containers/private-crm-app/PrivateSubmissionDetailPage/PrivateSubmissionDetailPage';

import ErrorPage from '@components/ErrorPage';
import Loading from '@components/Loading';

import { AuthScreen, useAuthManagerContext } from '@private-crm-app/auth-manager-context';
import LoginChoice from '@private-crm-app/components/LoginChoice';
import { LOGIN_REQUIRED, PrivateSubmissionAppRoutes } from '@private-crm-app/constants';
import LogoutHandler from '@private-crm-app/LogoutHandler/LogoutHandler';
import MissingTokenErrorPage from '@private-crm-app/MissingTokenErrorPage';
import NavigationErrorPage from '@private-crm-app/NavigationErrorPage';
import Onboarding from '@private-crm-app/Onboarding/Onboarding';
import PrivateSubmissionForm from '@private-crm-app/PrivateSubmissionForm/PrivateSubmissionForm';
import PrivateSubmissionListing from '@private-crm-app/PrivateSubmissionListing/PrivateSubmissionListing';
import PublicMapPage from '@private-crm-app/PublicMapPage';
import { get } from '@private-crm-app/requests/api';
import { useUserContext } from '@private-crm-app/user-context';
import UserProfile from '@private-crm-app/UserProfile/UserProfile';
import {
  ensureCitizenPortalVisited,
  getAgencyId,
  getCRMToken,
  getCRMTokenFromUrlOrStorage,
  getPrivateAppToken,
} from '@private-crm-app/utils';

interface PrivateSubmissionAppContainerProps {
  isSMSPasswordlessEnabled: boolean;
}

function PrivateSubmissionAppContainer({ isSMSPasswordlessEnabled }: PrivateSubmissionAppContainerProps) {
  const {
    isLoading: isAuthLoading,
    isAuthenticated,
    login,
    loginWithPhone,
    isValidatinQueryParams,
    refreshTrigger,
    isAuthResolved,
    hasAuth0Session,
  } = useAuthManagerContext();
  const { user, isLoading: isUserLoading } = useUserContext();
  const [showLoginChoice, setShowLoginChoice] = useState(false);
  const [loginChoiceMode, setLoginChoiceMode] = useState<'signin' | 'signup'>('signin');
  const [loginChoiceDismissed, setLoginChoiceDismissed] = useState(false);
  const skipLoginChoiceDismissResetRef = useRef(false);
  const history = useHistory();
  const location = useLocation();
  const [redirectAttempted, setRedirectAttempted] = useState(false);
  const [flagsLoaded, setFlagsLoaded] = useState(false);
  const [accountsFeatureEnabled, setAccountsFeatureEnabled] = useState<boolean>(false);
  const [isPublicMapEnabled, setIsPublicMapEnabled] = useState<boolean>(false);
  const [isOnboardingModalEnabled, setIsOnboardingModalEnabled] = useState<boolean>(true);

  // Load feature flags (citizen-accessible endpoint). Load when we have agencyId so PUBLIC_MAP
  // route can be gated even for unauthenticated users.
  useEffect(() => {
    let cancelled = false;

    const tryLoadFlags = async () => {
      const agencyId = getAgencyId();
      const hasToken = getPrivateAppToken() || getCRMTokenFromUrlOrStorage();
      console.log('[Container][Flags] Attempt', { agencyId, hasToken: !!hasToken, isValidatinQueryParams });

      if (!agencyId || !hasToken || isValidatinQueryParams) {
        if (!cancelled) {
          console.warn('[Container][Flags] Prerequisites not ready; retrying in 400ms');
          setTimeout(tryLoadFlags, 400);
        }

        return;
      }

      try {
        const url = `agency/${agencyId}/flags`;
        console.log('[Container][Flags] GET', url, { headers: { 'x-agency-id': agencyId } });
        const resp = await get<{
          isAccountsFeatureFlagEnabled: boolean;
          isPublicMapEnabled?: boolean;
          isOnboardingModalEnabled?: boolean;
        }>({
          url,
          headers: { 'x-agency-id': String(agencyId) },
        });
        if (cancelled) {
          return;
        }
        console.log('[Container][Flags] Response', resp);
        setAccountsFeatureEnabled(!!resp?.isAccountsFeatureFlagEnabled);
        setIsPublicMapEnabled(!!resp?.isPublicMapEnabled);
        setIsOnboardingModalEnabled(resp?.isOnboardingModalEnabled ?? true);
      } catch (e) {
        if (cancelled) {
          return;
        }
        console.error('[Container][Flags] Error loading flags', e?.response?.data || e?.message || e);
        setAccountsFeatureEnabled(false);
        setIsPublicMapEnabled(false);
      }

      if (!cancelled) {
        console.log('[Container][Flags] Flags loaded');
        setFlagsLoaded(true);
      }
    };

    if (isAuthResolved) {
      tryLoadFlags();
    }

    return () => {
      cancelled = true;
    };
  }, [isAuthResolved, isValidatinQueryParams, refreshTrigger]);

  // Once per 30-min idle session across all trackable portal surfaces.
  useEffect(() => {
    if (!isAuthResolved || isValidatinQueryParams) {
      return;
    }
    ensureCitizenPortalVisited(isAuthenticated, location.pathname);
  }, [isAuthResolved, isValidatinQueryParams, isAuthenticated, location.pathname]);

  useEffect(() => {
    // Defer any auth-based UI decisions until auth resolution completes to avoid modal flash on reload
    // Also wait until query params (like crm_token) are validated and stored to avoid race conditions
    if (!isAuthResolved || isValidatinQueryParams) {
      return;
    }
    const pathname = location.pathname;
    const search = location.search;
    const urlParams = new URLSearchParams(search);
    const isCallback = search.includes('code='); // Detect Auth0 redirect
    const hasVerifyToken = urlParams.get('verifyToken');

    if (pathname.includes(PrivateSubmissionAppRoutes.LOGOUT_HANDLER) || isCallback) {
      return; // Skip login during callback or logout
    }

    // Check if this is a route that requires CRM token
    const protectedRoutes = [
      PrivateSubmissionAppRoutes.LISTING,
      PrivateSubmissionAppRoutes.PROFILE,
      PrivateSubmissionAppRoutes.ISSUE_BASE_PATH,
      PrivateSubmissionAppRoutes.PUBLIC_MAP,
    ];

    const isProtectedRoute = protectedRoutes.some((route) => pathname.includes(route));

    if (isProtectedRoute) {
      // Check for CRM token in both localStorage and URL parameters
      const crmTokenFromStorage = getCRMToken();
      const urlParams = new URLSearchParams(search);
      const crmTokenFromUrl = urlParams.get('crm_token');

      // If CRM token is missing from both localStorage and URL, redirect to missing token error
      if (!crmTokenFromStorage && !crmTokenFromUrl) {
        window.location.href = `${process.env.APP_HOST_URL}${PrivateSubmissionAppRoutes.MISSING_TOKEN_ERROR}`;

        return;
      }
    }

    // Check for login_required parameter from email links
    const loginRequired = urlParams.get(LOGIN_REQUIRED) === 'true';
    const isIssueDetailPage = pathname.includes(PrivateSubmissionAppRoutes.ISSUE_BASE_PATH);

    // If coming from link with login_required=true, auto-select login flow based on login_choice or smsReceiptToken
    if (loginRequired && isIssueDetailPage && !isAuthenticated && !isAuthLoading) {
      console.log('Link detected with login_required=true, selecting authentication flow');
      const loginChoice = urlParams.get('login_choice');
      if (isSMSPasswordlessEnabled) {
        if (loginChoice === 'sms') {
          loginWithPhone(AuthScreen.ISSUE);

          return;
        }
        if (loginChoice === 'email') {
          login(AuthScreen.ISSUE);

          return;
        }
        const smsReceiptToken = urlParams.get('smsReceiptToken');
        if (smsReceiptToken) {
          loginWithPhone(AuthScreen.ISSUE);
        } else {
          setShowLoginChoice(true);
        }
      } else {
        login(AuthScreen.ISSUE);
      }

      return;
    }

    // Email verification requires the user to be authenticated to access profile and refresh user data
    // But we don't want to force login immediately as it might interfere with the verification process
    if (hasVerifyToken && !isAuthenticated && !isAuthLoading) {
      console.log('Email verification link detected, allowing UserProfile to handle verification');
      // Don't force login - let UserProfile handle the verification process
      // The verification will be processed in UserProfile component
      return;
    }

    // Show login choice ONLY for protected routes (listing, profile) when SMS is enabled
    const isListingPage = pathname.includes(PrivateSubmissionAppRoutes.LISTING);
    const isProfilePage = pathname.includes(PrivateSubmissionAppRoutes.PROFILE);
    const shouldAutoShowLoginChoice =
      isAuthResolved && (isListingPage || isProfilePage) && !isAuthenticated && !hasAuth0Session && !isAuthLoading;

    if (shouldAutoShowLoginChoice) {
      if (isSMSPasswordlessEnabled) {
        setLoginChoiceMode('signin');
        setShowLoginChoice(true);
      } else {
        // Legacy behavior: automatic login
        login(AuthScreen.LISTING);
      }
    } else {
      setLoginChoiceMode('signin');
      setShowLoginChoice(false);
    }
  }, [
    isAuthenticated,
    isAuthLoading,
    isSMSPasswordlessEnabled,
    login,
    loginWithPhone,
    isAuthResolved,
    isValidatinQueryParams,
    hasAuth0Session,
    location.pathname,
    location.search,
  ]);

  // Check for manual login choice trigger from navbar or submission flow
  useEffect(() => {
    if (!isAuthResolved) {
      return;
    }
    const shouldTriggerLoginChoice = localStorage.getItem('trigger_login_choice') === 'true';

    if (shouldTriggerLoginChoice && !isAuthenticated && !hasAuth0Session && isSMSPasswordlessEnabled) {
      const storedMode = localStorage.getItem('trigger_login_choice_mode') as 'signin' | 'signup' | null;
      setLoginChoiceDismissed(false);
      setLoginChoiceMode(storedMode ?? 'signin');
      setShowLoginChoice(true);
      localStorage.removeItem('trigger_login_choice');
      localStorage.removeItem('trigger_login_choice_mode');
    }
  }, [isAuthenticated, hasAuth0Session, isSMSPasswordlessEnabled, refreshTrigger, isAuthResolved]); // Check when auth state or refresh trigger changes

  useEffect(() => {
    if (skipLoginChoiceDismissResetRef.current) {
      skipLoginChoiceDismissResetRef.current = false;
      return;
    }
    setLoginChoiceDismissed(false);
  }, [location.pathname]);

  // Redirect to onboarding if needed - with improved handling for race conditions
  useEffect(() => {
    const pathname = location.pathname;

    // Only process if we're not already in the onboarding or logout path
    if (
      pathname.includes(PrivateSubmissionAppRoutes.ONBOARDING) ||
      pathname.includes(PrivateSubmissionAppRoutes.LOGOUT_HANDLER)
    ) {
      return;
    }

    // If authenticated and user data is loaded, check onboarding status (after flags are loaded)
    console.log('[Container][Onboarding Redirect Check]', {
      isAuthenticated,
      isUserLoading,
      hasUser: !!user,
      redirectAttempted,
      flagsLoaded,
      accountsFeatureEnabled,
      isOnboardingDone: user?.isOnboardingDone,
      pathname,
    });

    if (isAuthenticated && !isUserLoading && user && !redirectAttempted && flagsLoaded) {
      // Check if onboarding should be delayed due to toast showing
      const shouldDelayOnboarding = sessionStorage.getItem('delayOnboarding') === 'true';

      // Only redirect if isOnboardingDone is explicitly false (not undefined or true)
      if (
        user.isOnboardingDone === false &&
        accountsFeatureEnabled &&
        isOnboardingModalEnabled &&
        !shouldDelayOnboarding
      ) {
        console.log('User needs onboarding, redirecting to onboarding page');

        // Store the current path before redirecting to onboarding
        const currentPath = location.pathname;
        const currentSearch = location.search;
        if (currentPath.startsWith('/private-submission/') && currentPath !== PrivateSubmissionAppRoutes.ONBOARDING) {
          localStorage.setItem('auth_original_path', currentPath + currentSearch);
        }

        setRedirectAttempted(true);
        history.push(PrivateSubmissionAppRoutes.ONBOARDING);
      } else {
        // User doesn't need onboarding, reset redirect flag in case we need it later
        setRedirectAttempted(false);
      }
    }
  }, [
    isAuthenticated,
    isUserLoading,
    user,
    history,
    redirectAttempted,
    flagsLoaded,
    accountsFeatureEnabled,
    isOnboardingModalEnabled,
    location.pathname,
    location.search,
  ]);

  const pathname = location.pathname;
  const isPublicMapPath = pathname.includes(PrivateSubmissionAppRoutes.PUBLIC_MAP);
  const mustWaitForFlags = isPublicMapPath;

  if (
    !isAuthResolved ||
    (!isAuthenticated && isAuthLoading) ||
    isUserLoading ||
    isValidatinQueryParams ||
    (mustWaitForFlags && !flagsLoaded)
  ) {
    return <Loading show />;
  }

  const isListingPage = pathname.includes(PrivateSubmissionAppRoutes.LISTING);
  const isProfilePage = pathname.includes(PrivateSubmissionAppRoutes.PROFILE);
  const isIssueDetailRoute = pathname.includes(PrivateSubmissionAppRoutes.ISSUE_BASE_PATH);
  const isCreateSubmissionRoute = pathname.includes(PrivateSubmissionAppRoutes.CREATE);
  const shouldAutoShowLoginChoice =
    isAuthResolved && (isListingPage || isProfilePage) && !isAuthenticated && !hasAuth0Session && !isAuthLoading;
  const shouldRenderLoginChoice =
    isSMSPasswordlessEnabled &&
    !isAuthenticated &&
    !loginChoiceDismissed &&
    (showLoginChoice || shouldAutoShowLoginChoice);

  const handleCloseLoginChoice = () => {
    setLoginChoiceDismissed(true);
    setLoginChoiceMode('signin');
    setShowLoginChoice(false);

    // Profile requires authentication — closing the chooser must not leave the user
    // on an empty profile form. Redirect to listing and keep the chooser dismissed.
    if (isProfilePage) {
      skipLoginChoiceDismissResetRef.current = true;
      history.push(`${PrivateSubmissionAppRoutes.LISTING}${location.search}`);
    }
  };

  // Show login choice when SMS passwordless feature is enabled and user needs to authenticate
  if (shouldRenderLoginChoice) {
    const screen = isIssueDetailRoute
      ? AuthScreen.ISSUE
      : isCreateSubmissionRoute
      ? AuthScreen.CREATE_SUBMISSION_WITHOUT_ANONYMOUS
      : AuthScreen.LISTING;

    const mode: 'signin' | 'signup' = loginChoiceMode === 'signup' ? 'signup' : 'signin';

    return <LoginChoice screen={screen} mode={mode} onClose={handleCloseLoginChoice} />;
  }

  return (
    <Container fluid>
      <Switch>
        <Route exact path={PrivateSubmissionAppRoutes.ERROR} component={ErrorPage} />
        <Route exact path={PrivateSubmissionAppRoutes.NAVIGATION_ERROR} component={NavigationErrorPage} />
        <Route exact path={PrivateSubmissionAppRoutes.MISSING_TOKEN_ERROR} component={MissingTokenErrorPage} />
        <Route exact path={PrivateSubmissionAppRoutes.LISTING} component={PrivateSubmissionListing} />
        <Route exact path={PrivateSubmissionAppRoutes.CREATE} component={PrivateSubmissionForm} />
        <Route exact path={PrivateSubmissionAppRoutes.PROFILE} component={UserProfile} />
        <Route exact path={PrivateSubmissionAppRoutes.ISSUE_BASE_PATH} component={PrivateSubmissionDetailPage} />
        <Route exact path={PrivateSubmissionAppRoutes.ISSUE} component={PrivateSubmissionDetailPage} />
        {isPublicMapEnabled && <Route exact path={PrivateSubmissionAppRoutes.PUBLIC_MAP} component={PublicMapPage} />}
        <Route exact path={PrivateSubmissionAppRoutes.LOGOUT_HANDLER} component={LogoutHandler} />
        <Route exact path={PrivateSubmissionAppRoutes.ONBOARDING} component={Onboarding} />
        <Route component={ErrorPage} />
      </Switch>
    </Container>
  );
}

const mapStateToProps = (state: IReduxStore) => ({
  isSMSPasswordlessEnabled: true,
});

export default connect(mapStateToProps)(PrivateSubmissionAppContainer);
